import { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Link } from '@hivebrite/design-system/Link';
import { useMeasure } from '@hivebrite/react-hooks';

import { getIsExternalLink, getLink } from 'hb-builder/utils/links';
import type { PageImageItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/pageImageItemAttributess';

import { getXValue } from './utils/helpers';

interface ResizedImageUiProps {
  attributes: PageImageItemAttributes;
  hasLink: boolean;
}

const ResizedImageUi = ({
  attributes: {
    constructionColumnWidth,
    height,
    horizontalAlign,
    imageAlt,
    imageFullWidth,
    imageLink,
    imageLinkNewPage,
    imageRadius,
    imageSrc,
    left,
    top,
    width,
  },
  hasLink,
}: ResizedImageUiProps) => {
  const [ref, { width: wrapperWidth }] = useMeasure<HTMLDivElement>();
  const [position, setPosition] = useState({
    x: imageFullWidth ? 0 : left || 0,
    y: top || 0,
  });

  const [dimensions, setDimensions] = useState({
    height,
    width: imageFullWidth
      ? wrapperWidth
      : width || wrapperWidth * (constructionColumnWidth || 1),
  });

  const handleNonResizedImageShape = useCallback(() => {
    const imgNode: HTMLImageElement = new window.Image();
    const ratioTop = top
      ? (top * wrapperWidth) / (constructionColumnWidth || 1)
      : 0;

    imgNode.src = imageSrc || '';
    imgNode.onload = (e) => {
      const target = e.target as HTMLImageElement;
      const newHeight =
        target?.width < wrapperWidth
          ? target?.height
          : (wrapperWidth * (target?.height || 1)) / (target?.width || 1);

      const newWidth =
        imageFullWidth || target?.width >= wrapperWidth
          ? wrapperWidth
          : target?.width;

      setDimensions({
        height: newHeight,
        width: newWidth,
      });

      setPosition({
        x: imageFullWidth
          ? 0
          : getXValue({
              horizontalAlign,
              defaultValue: left || 0,
              imageWidth: newWidth,
              wrapperWidth,
            }),
        y: ratioTop,
      });
    };
  }, [
    constructionColumnWidth,
    horizontalAlign,
    imageFullWidth,
    imageSrc,
    left,
    top,
    wrapperWidth,
  ]);

  const handleResizedImageShape = useCallback(() => {
    const computedHeight = height || 0;
    const computedLeft = left || 0;
    const computedTop = top || 0;
    const computedWidth = width || 0;
    const ratioWidth = imageFullWidth
      ? wrapperWidth
      : (computedWidth * wrapperWidth) /
        (constructionColumnWidth || wrapperWidth);
    const ratioHeight =
      (computedHeight * wrapperWidth) /
      (constructionColumnWidth || wrapperWidth);
    const ratioTop =
      (computedTop * wrapperWidth) / (constructionColumnWidth || wrapperWidth);
    const ratioLeft =
      (computedLeft * wrapperWidth) / (constructionColumnWidth || wrapperWidth);

    setPosition({
      x: imageFullWidth
        ? 0
        : getXValue({
            horizontalAlign,
            defaultValue: ratioLeft,
            imageWidth: ratioWidth,
            wrapperWidth,
          }),
      y: ratioTop,
    });

    setDimensions({
      height: ratioHeight,
      width: ratioWidth,
    });
  }, [
    constructionColumnWidth,
    height,
    horizontalAlign,
    imageFullWidth,
    left,
    top,
    width,
    wrapperWidth,
  ]);

  const computedHeight = useMemo(
    () => (dimensions.height || 0) + (position.y || 0),
    [dimensions.height, position.y],
  );

  useEffect(() => {
    if (!wrapperWidth) {
      return;
    }
    if (!width || !height) {
      handleNonResizedImageShape();

      return;
    }
    handleResizedImageShape();
  }, [
    handleNonResizedImageShape,
    handleResizedImageShape,
    height,
    width,
    wrapperWidth,
  ]);

  const imageProps = {
    $borderRadius: imageRadius,
    $imageSrc: imageSrc,
    height: dimensions.height,
    left: `${position.x}px`,
    top: `${position.y}px`,
    width: dimensions.width,
  };

  const getContent = () => {
    if (hasLink && imageLink) {
      const domain = window.location.hostname;

      const linkProps = {
        forceDefaultLink: getIsExternalLink(imageLink, domain) || undefined,
        target: imageLinkNewPage ? '_blank' : '_self',
        to: getLink(imageLink, domain),
      };

      return (
        <Link {...linkProps} tabIndex={-1}>
          <ImgUi {...imageProps}>
            {imageAlt && <span role="img" aria-label={imageAlt} />}
          </ImgUi>
        </Link>
      );
    }

    return (
      <div tabIndex={-1}>
        <ImgUi {...imageProps}>
          {imageAlt && <span role="img" aria-label={imageAlt} />}
        </ImgUi>
      </div>
    );
  };

  return (
    <ImgWrapper $height={computedHeight} ref={ref}>
      {getContent()}
    </ImgWrapper>
  );
};

export default ResizedImageUi;

const ImgUi = styled(Box)<{ $borderRadius?: number; $imageSrc?: string }>`
  background-image: url(${({ $imageSrc }) => $imageSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: ${({ $borderRadius }) => $borderRadius || 0}px;
  position: absolute;
`;

const ImgWrapper = styled(Box).attrs(({ $height }: { $height: number }) => ({
  display: 'block',
  height: $height,
  width: '100%',
}))<{ $height: number }>`
  position: relative;
`;
